import { useState } from "react";
import { Quote, Star, PenLine } from "lucide-react";
import { WarmLightbox } from "./WarmLightbox";

interface WarmTestimonialProps {
  quote: string;
  author: string;
  relation: string;
  /** 1–5 star rating. Present in content data; now surfaced visually. */
  rating?: number;
  /** Optional scan of the original handwritten note/card. Opens in the lightbox.
   *  Left undefined until the family provides a real artifact — never fabricated. */
  noteImage?: string;
}

// Derive up to two initials from the author's display name (e.g. "Sarah M." -> "SM").
function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function WarmTestimonial({
  quote,
  author,
  relation,
  rating = 5,
  noteImage,
}: WarmTestimonialProps) {
  const [noteOpen, setNoteOpen] = useState(false);

  return (
    <article className="relative flex flex-col rounded-3xl border border-border/50 bg-card p-8 shadow-[var(--shadow-card)]">
      {/* Decorative quote mark */}
      <Quote className="absolute right-6 top-6 h-10 w-10 text-accent/20" />

      {/* Star rating — previously stored in data but never shown */}
      {rating > 0 && (
        <div className="mb-4 flex items-center gap-0.5" role="img" aria-label={`${rating} out of 5 stars`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${i < rating ? "fill-accent text-accent-strong" : "text-muted-foreground/30"}`}
            />
          ))}
        </div>
      )}

      <blockquote className="relative z-10 flex-1 font-heading text-lg italic leading-relaxed text-foreground">
        "{quote}"
      </blockquote>

      {/* Optional: view the original handwritten note in the lightbox */}
      {noteImage && (
        <button
          onClick={() => setNoteOpen(true)}
          className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-accent/10 px-3.5 py-1.5 text-sm font-medium text-accent-strong transition-colors hover:bg-accent/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <PenLine className="h-4 w-4" />
          View the handwritten note
        </button>
      )}

      <footer className="mt-6 flex items-center gap-4">
        {/* Initials monogram replaces the empty gray circle */}
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 font-heading text-base font-semibold text-primary">
          {getInitials(author)}
        </div>
        <div>
          <cite className="not-italic">
            <p className="font-semibold text-foreground">{author}</p>
            <p className="text-sm text-foreground/75">{relation}</p>
          </cite>
        </div>
      </footer>

      {noteImage && (
        <WarmLightbox
          items={[{ src: noteImage, title: `Note from ${author}`, category: relation }]}
          index={noteOpen ? 0 : null}
          onClose={() => setNoteOpen(false)}
          onIndexChange={() => {}}
        />
      )}
    </article>
  );
}
